import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';
import { useSearchParams } from 'next/navigation';
import { type FC, Fragment } from 'react';
import { BsChevronDown } from 'react-icons/bs';

import Button from 'molecules/Button';
import Icon, { iconIds } from 'molecules/Icon';

import styles from 'components/ListingDeck/Sidebar/sidebar.module.scss';

import { isNonEmptyArray } from 'utils/functions';

import type { IconIds } from 'molecules/Icon';

export interface SideBarItem {
  heading: string;
  content: {
    title: string;
    count: number;
  }[];
}

interface SidebarItemProps {
  items: SideBarItem[];
}

const Sidebar: FC<SidebarItemProps> = ({ items }) => {
  const searchParams = useSearchParams();

  if (!isNonEmptyArray(items)) {
    return null;
  }

  const updateQueryParams = (heading: string, value: string) => {
    const newParams = new URLSearchParams(searchParams.toString());
    const paramValue = newParams.get(heading);
    const pageParam = newParams.get('page');

    const updateSearchTerm = (paramValueToCheck: string | null) => {
      const values = paramValueToCheck ? paramValueToCheck.split(',') : [];
      if (values.includes(value)) {
        if (values.length === 1) {
          newParams.delete(heading);
        } else {
          newParams.set(heading, values.filter(v => v !== value).join(','));
        }
      } else {
        newParams.set(heading, [...values, value].join(','));
      }
    };

    updateSearchTerm(paramValue);

    if (pageParam) {
      newParams.set('page', '1');
    }

    window.history.pushState(null, '', `?${newParams.toString()}`);
  };

  const isValueSelected = (heading: string, value: string): boolean => {
    const param = searchParams.get(heading);
    if (!param) {
      return false;
    }

    return param.split(',').includes(value);
  };

  const shouldShowOption = (heading: string, value: string): boolean => {
    if (heading === 'Specialty' || heading === 'Content Type' || heading === 'Subject Matter') {
      return true; // Always show all options for Specialty
    }

    const param = searchParams.get(heading);
    if (!param) {
      return true;
    }

    if (param === value) {
      return true;
    }

    return false;
  };

  const clearAllFilters = () => {
    const newParams = new URLSearchParams(window.location.search);
    items.forEach(item => {
      if (newParams.has(item.heading)) {
        newParams.delete(item.heading);
      }
    });
    newParams.delete('page');
    newParams.delete('search');
    window.history.pushState(null, '', `?${newParams.toString()}`);
  };

  const filtersApplied =
    items.some(item => searchParams.has(item.heading)) || searchParams.has('page') || searchParams.has('search');

  return (
    <Fragment>
      <Accordion className={styles.root} type="multiple">
        {items.map(item => (
          <AccordionItem className={styles.accordionItem} key={item.heading} value={item.heading}>
            <AccordionTrigger className={styles.trigger}>
              {item.heading}
              <BsChevronDown className={styles.chevron} aria-hidden />
            </AccordionTrigger>
            <AccordionContent className={styles.content}>
              {item?.content.map(content => {
                const titleIsIcon = iconIds.includes(
                  `${content.title.toLowerCase().replace(/\s+/g, '')}-filter` as IconIds,
                );

                if (!shouldShowOption(item.heading, content.title) || content.count === 0) {
                  return null;
                }

                return (
                  <div key={content.title} className={styles.contentWrapper}>
                    <div key={content.title} className={styles.contentItem}>
                      <input
                        className={styles.checkbox}
                        type="checkbox"
                        id={content.title}
                        name={content.title}
                        value={content.title}
                        checked={isValueSelected(item.heading, content.title)}
                        onChange={() => updateQueryParams(item.heading, content.title)}
                      />
                      <label className={styles.contentLabel} htmlFor={content.title}>{`${content.title}`}</label>
                    </div>
                    {titleIsIcon && (
                      <Icon icon={`${content.title.toLowerCase().replace(/\s+/g, '')}-filter` as IconIds} size={20} />
                    )}
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {filtersApplied && <Button label="Clear All Filters" className={styles.button} onClick={clearAllFilters} />}
    </Fragment>
  );
};

export default Sidebar;
